import * as React from 'react';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import { EventDataChannel } from 'types/api/Tenant/EventDataSourceTypes';
import { Chip, Divider, Stack, Box } from '@mui/material';
import { ButtonGroupSplit } from 'components/ButtonComponent/ButtonGroupSplit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

interface ChannelCardProps {
  channelItem: EventDataChannel;
  onSelectChannel: (selectedChannel: EventDataChannel, actionKey: string) => void;
}

export const BUTTON_ACTION_CHANNEL_ITEM_KEY = {
  CONNECT_CHANNEL: 'CONNECT_CHANNEL',
  PREVIEW_DATA: 'PREVIEW_DATA',
};

const BUTTON_ACTION_OPTIONS = [
  {
    label: 'Connect Channel',
    value: BUTTON_ACTION_CHANNEL_ITEM_KEY.CONNECT_CHANNEL,
  },
  {
    label: 'Preview Data',
    value: BUTTON_ACTION_CHANNEL_ITEM_KEY.PREVIEW_DATA,
  },
];

export function ChannelCard(props: ChannelCardProps) {
  const { channelItem, onSelectChannel } = props;

  const handleOnSelectOption = (optionKey: string) => {
    onSelectChannel(channelItem, optionKey);
  };

  const isInactive = channelItem.status === 'INACTIVE';
  const isConnected = channelItem.isConnected;

  return (
    <Card
      variant='outlined'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: isConnected ? '2px solid #42BB93' : '1px solid #E3EBF6',
        transition: 'all 0.2s ease',
        opacity: isInactive ? 0.6 : 1,
        '&:hover': {
          borderColor: isInactive ? '#E3EBF6' : '#42BB93',
          boxShadow: isInactive ? 'none' : '0 4px 16px rgba(59, 71, 151, 0.1)',
          transform: isInactive ? 'none' : 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 2.5, pb: 1.5 }}>
        <Stack direction='row' gap={2} justifyContent='space-between' alignItems='flex-start'>
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              bgcolor: '#F8F9FC',
              border: '1px solid #E3EBF6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 56,
              width: '55%',
            }}
          >
            <CardMedia
              sx={{ height: 40, width: '100%', objectFit: 'contain' }}
              component='img'
              image={channelItem.logoUrl ? channelItem.logoUrl : '/images/main-logo.svg'}
              style={{ filter: isInactive ? 'grayscale(100%)' : 'none' }}
              title={channelItem.name}
            />
          </Box>
          <Stack direction='row' gap={0.5}>
            {isConnected && (
              <Chip
                icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important' }} />}
                label='Connected'
                size='small'
                sx={{
                  bgcolor: '#EEF7F3',
                  color: '#42BB93',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 24,
                  borderRadius: 1,
                  '& .MuiChip-icon': { color: '#42BB93' },
                }}
              />
            )}
            {isInactive && (
              <Chip
                label='Inactive'
                size='small'
                sx={{
                  bgcolor: '#FFF3E0',
                  color: '#E65100',
                  fontWeight: 600,
                  fontSize: 11,
                  height: 24,
                  borderRadius: 1,
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack mt={1.5} height={50}>
          <Typography
            gutterBottom
            variant='h6'
            fontSize={15}
            fontWeight='bold'
            sx={{ color: '#141414', lineHeight: 1.3 }}
          >
            {channelItem.name}
          </Typography>
        </Stack>

        <Stack height={80}>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 13, lineHeight: 1.5 }}>
            {channelItem.description}
          </Typography>
        </Stack>
      </CardContent>

      <Divider sx={{ borderColor: '#E3EBF6' }} />

      <CardActions sx={{ p: 1.5 }}>
        <ButtonGroupSplit
          disabled={isInactive}
          text='Select Channel'
          options={BUTTON_ACTION_OPTIONS}
          onSelectOption={handleOnSelectOption}
        />
      </CardActions>
    </Card>
  );
}
